import React, { useCallback, useEffect, useState } from "react";
import {
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { TEST_TYPES } from "../../../constants/testConstants";
import type {
  TestTypeViolationPolicy,
  ViolationAttemptRule,
} from "./testTypes";
import { fetchViolationPolicies, updateViolationPolicy } from "./testUtils";

const headCellSx = {
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "text.secondary",
  bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};

const bodyCellSx = {
  fontSize: "0.8125rem",
  color: "#333",
};

const defaultPolicyFor = (testType: string): TestTypeViolationPolicy => ({
  test_type: testType,
  max_attempts: 4,
  penalty_rules: [
    { attempt: 1, penalty_percent: 0, terminate: false },
    { attempt: 2, penalty_percent: 25, terminate: false },
    { attempt: 3, penalty_percent: 50, terminate: false },
    { attempt: 4, penalty_percent: 100, terminate: true },
  ],
  is_persisted: false,
  updated_at: null,
});

const normalizePolicies = (
  incoming: TestTypeViolationPolicy[]
): TestTypeViolationPolicy[] =>
  TEST_TYPES.map((testType) => {
    const found = incoming.find((row) => row.test_type === testType);
    if (!found) return defaultPolicyFor(testType);
    return {
      test_type: testType,
      max_attempts: Number(found.max_attempts ?? 1),
      penalty_rules: (found.penalty_rules ?? []).map((rule) => ({
        attempt: Number(rule.attempt),
        penalty_percent: Number(rule.penalty_percent ?? 0),
        terminate: Boolean(rule.terminate),
      })),
      is_persisted: Boolean(found.is_persisted),
      updated_at: found.updated_at ?? null,
    };
  });

const formatUpdatedAt = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const validatePolicy = (
  maxAttempts: number,
  penaltyRules: ViolationAttemptRule[]
): string | null => {
  if (maxAttempts < 1) return "Max attempts must be at least 1.";
  if (penaltyRules.length === 0) return "At least one penalty rule is required.";
  const attempts = penaltyRules.map((rule) => rule.attempt);
  if (new Set(attempts).size !== attempts.length) {
    return "Each attempt number must be unique.";
  }
  for (const attempt of attempts) {
    if (attempt < 1 || attempt > maxAttempts) {
      return `Attempt ${attempt} must be between 1 and ${maxAttempts}.`;
    }
  }
  const highestAttempt = Math.max(...attempts);
  if (highestAttempt !== maxAttempts) {
    return `Highest penalty rule attempt must equal max attempts (${maxAttempts}).`;
  }
  return null;
};

const getApiErrorDetail = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error) && error.response?.data?.detail) {
    return String(error.response.data.detail);
  }
  return fallback;
};

const clonePolicy = (policy: TestTypeViolationPolicy): TestTypeViolationPolicy => {
  const penalty_rules = policy.penalty_rules.map((rule) => ({ ...rule }));
  const lastIndex = penalty_rules.length - 1;
  return {
    ...policy,
    penalty_rules: penalty_rules.map((rule, index) => ({
      ...rule,
      terminate: index === lastIndex ? rule.terminate : false,
    })),
  };
};

const ViolationPolicyTable: React.FC = () => {
  const [policies, setPolicies] = useState<TestTypeViolationPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<TestTypeViolationPolicy | null>(
    null
  );
  const [editError, setEditError] = useState<string | null>(null);

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchViolationPolicies();
      setPolicies(normalizePolicies(data.policies ?? []));
    } catch {
      setError("Failed to load violation policies.");
      setPolicies(normalizePolicies([]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const openEdit = (policy: TestTypeViolationPolicy) => {
    setEditingPolicy(clonePolicy(policy));
    setEditError(null);
    setSavedMessage(null);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditingPolicy(null);
    setEditError(null);
  };

  const updateEditingPolicy = (
    updater: (current: TestTypeViolationPolicy) => TestTypeViolationPolicy
  ) => {
    setEditingPolicy((current) => (current ? updater(current) : current));
    setEditError(null);
  };

  const handleMaxAttemptsChange = (value: string) => {
    const parsed = value === "" ? 1 : Math.max(1, Number(value) || 1);
    updateEditingPolicy((policy) => {
      // Keep rules in sync with max_attempts so Save isn't blocked by stale 5..N rows
      const nextRules = Array.from({ length: parsed }, (_, index) => {
        const attempt = index + 1;
        const existing = policy.penalty_rules.find((rule) => rule.attempt === attempt);
        const isLast = attempt === parsed;
        return {
          attempt,
          penalty_percent: existing?.penalty_percent ?? 0,
          terminate: isLast ? Boolean(existing?.terminate ?? true) : false,
        };
      });
      return { ...policy, max_attempts: parsed, penalty_rules: nextRules };
    });
  };

  const handleRuleChange = (
    index: number,
    field: "attempt" | "penalty_percent" | "terminate",
    value: string | boolean
  ) => {
    updateEditingPolicy((policy) => {
      const rules = [...policy.penalty_rules];
      const current = rules[index] ?? {
        attempt: index + 1,
        penalty_percent: 0,
        terminate: false,
      };
      if (field === "terminate") {
        rules[index] = { ...current, terminate: Boolean(value) };
      } else {
        rules[index] = {
          ...current,
          [field]: Math.max(0, Number(value) || 0),
        };
      }
      return { ...policy, penalty_rules: rules };
    });
  };

  const addRule = () => {
    updateEditingPolicy((policy) => ({
      ...policy,
      penalty_rules: [
        ...policy.penalty_rules.map((rule) => ({ ...rule, terminate: false })),
        {
          attempt: policy.penalty_rules.length + 1,
          penalty_percent: 0,
          terminate: false,
        },
      ],
    }));
  };

  const removeRule = (index: number) => {
    updateEditingPolicy((policy) => ({
      ...policy,
      penalty_rules: policy.penalty_rules.filter((_, idx) => idx !== index),
    }));
  };

  const handleSave = async () => {
    if (!editingPolicy) return;

    const validationError = validatePolicy(
      editingPolicy.max_attempts,
      editingPolicy.penalty_rules
    );
    if (validationError) {
      setEditError(validationError);
      return;
    }

    setSaving(true);
    setEditError(null);
    setError(null);
    setSavedMessage(null);
    const updatedTestType = editingPolicy.test_type;
    try {
      await updateViolationPolicy(editingPolicy.test_type, {
        max_attempts: editingPolicy.max_attempts,
        penalty_rules: editingPolicy.penalty_rules,
      });
      setEditingPolicy(null);
      await loadPolicies();
      setSavedMessage(`${updatedTestType} policy updated successfully.`);
    } catch (saveError) {
      setEditError(
        getApiErrorDetail(saveError, "Failed to save violation policy.")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-1">Violation Policies</h5>
      <p className="text-muted small mb-3">
        Configure max violation attempts and score penalties per test type.
      </p>

      {error && (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      )}
      {savedMessage && (
        <Alert variant="success" className="py-2">
          {savedMessage}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Test Type</TableCell>
              <TableCell sx={headCellSx}>Max Attempts</TableCell>
              <TableCell sx={headCellSx}>Penalty Rules</TableCell>
              <TableCell sx={headCellSx}>Last Updated</TableCell>
              <TableCell sx={{ ...headCellSx, width: 90 }} align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map((policy) => (
              <TableRow key={policy.test_type} hover>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                  {policy.test_type}
                </TableCell>
                <TableCell sx={bodyCellSx}>{policy.max_attempts}</TableCell>
                <TableCell sx={bodyCellSx}>{policy.penalty_rules.length}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: "#666" }}>
                  {formatUpdatedAt(policy.updated_at)}
                </TableCell>
                <TableCell sx={bodyCellSx} align="center">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openEdit(policy)}
                    title={`Edit ${policy.test_type}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        show={Boolean(editingPolicy)}
        onHide={closeEdit}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            Edit policy — {editingPolicy?.test_type ?? ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" className="py-2">
              {editError}
            </Alert>
          )}
          {editingPolicy && (
            <>
              <div className="d-flex align-items-end justify-content-between gap-3 mb-3">
                <Form.Group style={{ maxWidth: 200 }}>
                  <Form.Label>Max attempts</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={editingPolicy.max_attempts}
                    onChange={(event) =>
                      handleMaxAttemptsChange(event.target.value)
                    }
                    disabled={saving}
                  />
                </Form.Group>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addRule}
                  disabled={saving}
                >
                  + Rule
                </Button>
              </div>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Attempt</TableCell>
                      <TableCell sx={headCellSx}>Penalty %</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: "center" }}>
                        Terminate
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, width: 72 }} align="center">
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editingPolicy.penalty_rules.map((rule, index) => {
                      const isLastRule =
                        index === editingPolicy.penalty_rules.length - 1;
                      return (
                      <TableRow key={`${editingPolicy.test_type}-${index}`}>
                        <TableCell sx={bodyCellSx}>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 1, max: editingPolicy.max_attempts }}
                            value={rule.attempt}
                            onChange={(event) =>
                              handleRuleChange(index, "attempt", event.target.value)
                            }
                            disabled={saving}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, max: 100 }}
                            value={rule.penalty_percent}
                            onChange={(event) =>
                              handleRuleChange(
                                index,
                                "penalty_percent",
                                event.target.value
                              )
                            }
                            disabled={saving}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="center">
                          {isLastRule ? (
                            <Checkbox
                              checked={rule.terminate}
                              onChange={(event) =>
                                handleRuleChange(
                                  index,
                                  "terminate",
                                  event.target.checked
                                )
                              }
                              disabled={saving}
                              size="small"
                            />
                          ) : null}
                        </TableCell>
                        <TableCell sx={bodyCellSx} align="center">
                          {isLastRule ? (
                            <IconButton
                              size="small"
                              color="error"
                              title="Remove rule"
                              disabled={
                                saving || editingPolicy.penalty_rules.length <= 1
                              }
                              onClick={() => removeRule(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ViolationPolicyTable;
